import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Lock, Sparkles, Check, Star, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';
import { useLanguage } from '../../i18n/LanguageContext';
import { ZodiacTheme } from './zodiacThemes';
interface CalendarTabProps {
  theme: ZodiacTheme;
}
interface DayData {
  day: number;
  moonPhase?: string;
  moonLabel?: string;
  quality: 'good' | 'neutral' | 'hard';
  forecast: string;
  proTransits?: string[];
  proAdvice?: string;
}
const MONTH_NAMES = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const QUALITY_STYLES = {
  good: {
    dot: 'bg-emerald-400',
    text: 'text-emerald-700',
    bg: 'bg-emerald-50',
    label: 'Благоприятный'
  },
  neutral: {
    dot: 'bg-slate-300',
    text: 'text-slate-600',
    bg: 'bg-slate-50',
    label: 'Нейтральный'
  },
  hard: {
    dot: 'bg-rose-400',
    text: 'text-rose-700',
    bg: 'bg-rose-50',
    label: 'Сложный'
  }
};
function generateMonthData(year: number, month: number): DayData[] {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const forecasts = ['Хороший день для новых начинаний и планирования.', 'Сосредоточьтесь на рутинных задачах, избегайте рисков.', 'Энергия дня способствует общению и переговорам.', 'Уделите время отдыху и восстановлению сил.', 'Благоприятный момент для финансовых решений.', 'Возможны неожиданные перемены, будьте гибкими.', 'Отличный день для творчества и самовыражения.', 'Стоит проявить осторожность в отношениях.', 'Продуктивный день, доводите дела до конца.', 'Интуиция обострена — доверяйте внутреннему голосу.'];
  const proTransitSets = [['Трин Венера-Юпитер', 'Секстиль Меркурий-Нептун'], ['Квадрат Марс-Сатурн'], ['Соединение Солнце-Меркурий', 'Трин Луна-Венера'], ['Оппозиция Венера-Плутон'], ['Секстиль Марс-Юпитер']];
  const proAdvices = ['Идеальный день для важных встреч и подписания договоров.', 'Избегайте конфликтов и импульсивных трат.', 'Хорошее время для начала нового проекта.', 'Посвятите вечер медитации и самоанализу.', 'Удачный момент для романтических жестов.'];
  const data: DayData[] = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const seed = (d * 7 + month * 13 + year) % 10;
    const quality: 'good' | 'neutral' | 'hard' = seed < 4 ? 'good' : seed < 8 ? 'neutral' : 'hard';
    let moonPhase: string | undefined;
    let moonLabel: string | undefined;
    if (d === 2) {
      moonPhase = '🌑';
      moonLabel = 'Новолуние';
    } else if (d === 10) {
      moonPhase = '🌓';
      moonLabel = 'Первая четверть';
    } else if (d === 17) {
      moonPhase = '🌕';
      moonLabel = 'Полнолуние';
    } else if (d === 24) {
      moonPhase = '🌗';
      moonLabel = 'Последняя четверть';
    }
    data.push({
      day: d,
      moonPhase,
      moonLabel,
      quality,
      forecast: forecasts[seed],
      proTransits: d % 3 === 0 ? proTransitSets[d % 5] : undefined,
      proAdvice: d % 4 === 0 ? proAdvices[d % 5] : undefined
    });
  }
  return data;
}
export function CalendarTab({
  theme
}: CalendarTabProps) {
  const {
    showToast
  } = useToast();
  const {
    t
  } = useLanguage();
  const [currentDate, setCurrentDate] = useState(new Date(2024, 9, 15)); // Oct 15, 2024
  const [selectedDay, setSelectedDay] = useState<number | null>(15);
  const [showProModal, setShowProModal] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [showProBanner, setShowProBanner] = useState(true);
  const monthData = useMemo(() => generateMonthData(currentDate.getFullYear(), currentDate.getMonth()), [currentDate]);
  const firstDayOfWeek = useMemo(() => {
    const d = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
    return d === 0 ? 6 : d - 1; // Convert Sunday=0 to Monday-based
  }, [currentDate]);
  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === currentDate.getFullYear() && today.getMonth() === currentDate.getMonth();
  const todayDate = isCurrentMonth ? today.getDate() : -1;
  const selectedDayData = selectedDay ? monthData.find(d => d.day === selectedDay) : null;
  const prevMonth = () => {
    if (currentDate.getMonth() === 0) {
      setCurrentDate(new Date(currentDate.getFullYear() - 1, 11, 1));
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    }
    setSelectedDay(null);
  };
  const nextMonth = () => {
    if (currentDate.getMonth() === 11) {
      setCurrentDate(new Date(currentDate.getFullYear() + 1, 0, 1));
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    }
    setSelectedDay(null);
  };
  const calendarCells: (DayData | null)[] = [];
  for (let i = 0; i < firstDayOfWeek; i++) calendarCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    calendarCells.push(monthData.find(dd => dd.day === d) || null);
  }
  while (calendarCells.length % 7 !== 0) calendarCells.push(null);
  return <div className="space-y-6 pb-8" data-id="element-18509">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-18510">
        <div data-id="element-18511">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-18512">
            {t('userdash.calendar.title')}
          </h2>
          <p className="text-sm text-slate-500 mt-1" data-id="element-18513">
            {MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-18514">
          <Button variant="outline" size="sm" onClick={() => {
          setCurrentDate(new Date(2024, 9, 15));
          setSelectedDay(15);
        }} data-id="element-18515">
            {t('userdash.calendar.today')}
          </Button>
          <button onClick={prevMonth} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-18516">
            <ChevronLeft className="w-4 h-4 text-slate-600" data-id="element-18517" />
          </button>
          <span className="text-sm font-bold text-slate-900 min-w-[140px] text-center" data-id="element-18518">
            {MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}
          </span>
          <button onClick={nextMonth} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-18519">
            <ChevronRight className="w-4 h-4 text-slate-600" data-id="element-18520" />
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-[11px] text-slate-500" data-id="element-18521">
        <span className="flex items-center gap-1" data-id="element-18522">
          <span className="w-2 h-2 rounded-full bg-emerald-400" data-id="element-18523" /> Благоприятный
        </span>
        <span className="flex items-center gap-1" data-id="element-18524">
          <span className="w-2 h-2 rounded-full bg-slate-300" data-id="element-18525" /> Нейтральный
        </span>
        <span className="flex items-center gap-1" data-id="element-18526">
          <span className="w-2 h-2 rounded-full bg-rose-400" data-id="element-18527" /> Сложный
        </span>
        <span className="flex items-center gap-1" data-id="element-18528">🌕 Лунная фаза</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-id="element-18529">
        {/* Calendar Grid */}
        <div className="lg:col-span-2" data-id="element-18530">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-id="element-18531">
            {/* Weekday headers */}
            <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50" data-id="element-18532">
              {WEEKDAYS.map(wd => <div key={wd} className="py-2 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider" data-id="element-18533">
                  {wd}
                </div>)}
            </div>
            {/* Days grid */}
            <div className="grid grid-cols-7" data-id="element-18534">
              {calendarCells.map((cell, i) => {
              if (!cell) {
                return <div key={`empty-${i}`} className="min-h-[72px] sm:min-h-[80px] bg-slate-50/50 border-b border-r border-slate-100" data-id="element-18535" />;
              }
              const isToday = cell.day === todayDate;
              const isSelected = cell.day === selectedDay;
              const q = QUALITY_STYLES[cell.quality];
              return <button key={cell.day} onClick={() => setSelectedDay(cell.day)} className={`min-h-[72px] sm:min-h-[80px] p-1.5 sm:p-2 text-left border-b border-r border-slate-100 transition-colors relative ${isSelected ? 'bg-indigo-50/60 ring-1 ring-inset ring-indigo-300' : 'hover:bg-slate-50'} ${isToday ? 'bg-amber-50/40' : ''}`} data-id="element-18536">
                    <div className="flex items-center justify-between mb-1" data-id="element-18537">
                      <span className={`text-xs sm:text-sm font-medium ${isToday ? 'w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-[11px]' : 'text-slate-700'}`} data-id="element-18538">
                        {cell.day}
                      </span>
                      <span className={`w-1.5 h-1.5 rounded-full ${q.dot}`} data-id="element-18539" />
                    </div>
                    {cell.moonPhase && <div className="text-xs sm:text-sm leading-none mb-0.5" data-id="element-18540">
                        {cell.moonPhase}
                      </div>}
                    {cell.proTransits && !isPro && <div className="absolute bottom-1 right-1.5" data-id="element-18541">
                        <Lock className="w-2.5 h-2.5 text-slate-300" data-id="element-18542" />
                      </div>}
                    {cell.proTransits && isPro && <div className="text-[8px] sm:text-[9px] text-indigo-600 truncate mt-0.5 hidden sm:block" data-id="element-18543">
                        {cell.proTransits[0]}
                      </div>}
                  </button>;
            })}
            </div>
          </div>
        </div>

        {/* Day Detail Panel */}
        <div className="lg:col-span-1 space-y-4" data-id="element-18544">
          {selectedDayData ? <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-18545">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200" data-id="element-18546">
                <div className="flex items-center justify-between" data-id="element-18547">
                  <div data-id="element-18548">
                    <div className="text-lg font-bold text-slate-900" data-id="element-18549">
                      {selectedDayData.day}{' '}
                      {MONTH_NAMES[currentDate.getMonth()]}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-18550">
                      {selectedDayData.moonLabel || QUALITY_STYLES[selectedDayData.quality].label + ' день'}
                    </div>
                  </div>
                  {selectedDayData.moonPhase && <span className="text-2xl" data-id="element-18551">
                      {selectedDayData.moonPhase}
                    </span>}
                </div>
              </div>

              <div className="p-4 space-y-4" data-id="element-18552">
                {/* Quality */}
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${QUALITY_STYLES[selectedDayData.quality].bg}`} data-id="element-18553">
                  <span className={`w-2.5 h-2.5 rounded-full ${QUALITY_STYLES[selectedDayData.quality].dot}`} data-id="element-18554" />
                  <span className={`text-xs font-medium ${QUALITY_STYLES[selectedDayData.quality].text}`} data-id="element-18555">
                    {QUALITY_STYLES[selectedDayData.quality].label} день
                  </span>
                </div>

                {/* Free forecast */}
                <div data-id="element-18556">
                  <h4 className="text-xs font-bold text-slate-900 mb-1.5" data-id="element-18557">
                    Прогноз дня
                  </h4>
                  <p className="text-xs text-slate-600 leading-relaxed" data-id="element-18558">
                    {selectedDayData.forecast}
                  </p>
                </div>

                {/* Moon phase info */}
                {selectedDayData.moonLabel && <div className="flex items-center gap-2 p-2.5 bg-amber-50 rounded-lg" data-id="element-18559">
                    <span className="text-lg" data-id="element-18560">{selectedDayData.moonPhase}</span>
                    <div data-id="element-18561">
                      <div className="text-xs font-medium text-amber-900" data-id="element-18562">
                        {selectedDayData.moonLabel}
                      </div>
                      <div className="text-[10px] text-amber-700" data-id="element-18563">
                        Влияет на эмоции и интуицию
                      </div>
                    </div>
                  </div>}

                {/* PRO: Transits */}
                {selectedDayData.proTransits && <div className="relative" data-id="element-18564">
                    <div className="flex items-center justify-between mb-1.5" data-id="element-18565">
                      <h4 className="text-xs font-bold text-slate-900" data-id="element-18566">
                        Персональные транзиты
                      </h4>
                      {!isPro && <Badge variant="pro" size="sm" className="text-[8px] py-0 h-4" data-id="element-18567">
                          PRO
                        </Badge>}
                    </div>
                    {isPro ? <div className="space-y-1.5" data-id="element-18568">
                        {selectedDayData.proTransits.map((t, i) => <div key={i} className="text-xs text-indigo-700 bg-indigo-50 px-2.5 py-1.5 rounded-lg" data-id="element-18569">
                            {t}
                          </div>)}
                      </div> : <div className="relative" data-id="element-18570">
                        <div className="space-y-1.5 blur-[3px] select-none pointer-events-none" data-id="element-18571">
                          {selectedDayData.proTransits.map((t, i) => <div key={i} className="text-xs text-slate-500 bg-slate-100 px-2.5 py-1.5 rounded-lg" data-id="element-18572">
                              {t}
                            </div>)}
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center" data-id="element-18573">
                          <button onClick={() => {
                    setIsPro(true);
                    showToast('PRO Календарь активирован!');
                  }} className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-indigo-200 rounded-lg text-xs font-medium text-indigo-700 shadow-sm hover:bg-indigo-50 transition-colors" data-id="element-18574">
                            <Lock className="w-3 h-3" data-id="element-18575" /> Разблокировать
                          </button>
                        </div>
                      </div>}
                  </div>}

                {/* PRO: Advice */}
                {selectedDayData.proAdvice && <div className="relative" data-id="element-18576">
                    <div className="flex items-center justify-between mb-1.5" data-id="element-18577">
                      <h4 className="text-xs font-bold text-slate-900" data-id="element-18578">
                        Рекомендация
                      </h4>
                      {!isPro && <Badge variant="pro" size="sm" className="text-[8px] py-0 h-4" data-id="element-18579">
                          PRO
                        </Badge>}
                    </div>
                    {isPro ? <p className="text-xs text-slate-700 bg-emerald-50 px-3 py-2 rounded-lg leading-relaxed" data-id="element-18580">
                        {selectedDayData.proAdvice}
                      </p> : <div className="relative" data-id="element-18581">
                        <p className="text-xs text-slate-400 bg-slate-50 px-3 py-2 rounded-lg blur-[3px] select-none" data-id="element-18582">
                          {selectedDayData.proAdvice}
                        </p>
                        <div className="absolute inset-0 flex items-center justify-center" data-id="element-18583">
                          <Lock className="w-4 h-4 text-slate-300" data-id="element-18584" />
                        </div>
                      </div>}
                  </div>}
              </div>
            </div> : <div className="bg-white border border-slate-200 rounded-xl p-6 text-center shadow-sm" data-id="element-18585">
              <div className="text-2xl mb-2" data-id="element-18586">📅</div>
              <p className="text-xs text-slate-500" data-id="element-18587">
                Выберите день в календаре
              </p>
            </div>}

          {/* PRO Banner */}
          {!isPro && showProBanner && <div className="bg-gradient-to-br from-indigo-50 to-white border border-indigo-200 rounded-xl p-4 relative" data-id="element-18588">
              <button onClick={() => setShowProBanner(false)} className="absolute top-2 right-2 p-1 text-slate-400 hover:text-slate-600" data-id="element-18589">
                <X className="w-3.5 h-3.5" data-id="element-18590" />
              </button>
              <Badge variant="pro" size="sm" className="mb-2" data-id="element-18591">
                PRO
              </Badge>
              <h4 className="font-bold text-slate-900 text-sm mb-1" data-id="element-18592">
                Персональный календарь
              </h4>
              <p className="text-[11px] text-slate-600 mb-3 leading-relaxed" data-id="element-18593">
                Транзиты, рекомендации и предупреждения на основе вашей
                натальной карты.
              </p>
              <ul className="space-y-1.5 mb-3" data-id="element-18594">
                <li className="flex items-center gap-1.5 text-[11px] text-slate-700" data-id="element-18595">
                  <Check className="w-3 h-3 text-emerald-500 shrink-0" data-id="element-18596" />{' '}
                  Индивидуальные дни
                </li>
                <li className="flex items-center gap-1.5 text-[11px] text-slate-700" data-id="element-18597">
                  <Check className="w-3 h-3 text-emerald-500 shrink-0" data-id="element-18598" />{' '}
                  Предупреждения
                </li>
                <li className="flex items-center gap-1.5 text-[11px] text-slate-700" data-id="element-18599">
                  <Check className="w-3 h-3 text-emerald-500 shrink-0" data-id="element-18600" /> Google
                  Calendar
                </li>
              </ul>
              <Button variant="primary" size="sm" className="w-full text-xs" onClick={() => {
            setIsPro(true);
            showToast('PRO Календарь активирован!');
          }} data-id="element-18601">
                <Sparkles className="w-3.5 h-3.5 mr-1" data-id="element-18602" /> Подключить за 990
                ₽/мес
              </Button>
            </div>}
        </div>
      </div>
    </div>;
}